import React, { Component } from "react";
import { createPortal } from "react-dom";
import { AxiosResponse } from "axios";
import NewsletterForm from "../../components/Newsletter/NewsletterForm";
import { getRequest, postRequest } from "../../utils/httpRequest";

// Nothing fires until the reader has stuck around a while, so a bounce never
// gets interrupted.
const ARM_DELAY_MS = 15000;
// Reading progress stands in for exit intent on touch devices, which have no
// cursor to track.
const SCROLL_TRIGGER_RATIO = 0.6;
// Course pages ask for more of the page first. Someone weighing up a purchase is
// closer to buying than anyone else on the site, so we only speak up once they
// are clearly on their way past it.
const COURSE_SCROLL_TRIGGER_RATIO = 0.7;
// Time spent actually looking at the site, accumulated across pages. This is the
// trigger that catches the people exit intent never sees: anyone leaving by the
// back button, by closing the tab, or by wandering off to another app.
const DWELL_TRIGGER_MS = 60000;
const DWELL_TICK_MS = 1000;
// How long the "you're in" confirmation stays up before we get out of the way.
const CONFIRMATION_MS = 6000;

// One appearance per tab, however many pages the visitor moves through.
const SESSION_KEY = "42c_blog_newsletter_popup_shown";
const DWELL_KEY = "42c_newsletter_popup_dwell_ms";
const PAGEVIEWS_KEY = "42c_newsletter_popup_pageviews";
const LAST_PATH_KEY = "42c_newsletter_popup_last_path";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';

type Trigger = "exit_intent" | "scroll_depth" | "dwell" | "second_page";

// The two that mean "on the way out". Course pages allow nothing else: someone
// weighing up a purchase should not be interrupted mid-thought.
const LEAVING_TRIGGERS: Trigger[] = ["exit_intent", "scroll_depth"];

interface Props {
  // Mirrors the promo bar's context so the two nudges can apply different rules
  // on the page where they would otherwise compete.
  pageContext?: "homepage" | "course" | "logged-in" | "checkout";
  // Changes on client-side navigation, which is how a page view gets counted in
  // an app that never reloads.
  pathname?: string;
}

// Turns "Let us nourish *your noggin*" into the heading with the yellow
// highlighter behind the starred phrase. An <em> rather than a <span> because
// the popup, the home page and the blog band already style em that way, and
// because it is genuinely emphasis. Odd segments of the split are the marked
// ones; unmatched asterisks simply render as themselves.
const withHighlight = (text: string) =>
  text
    .split(/\*([^*]+)\*/)
    .map((part, index) =>
      index % 2 === 1 ? <em key={index}>{part}</em> : part,
    );

// Edited in admin under Site Content → Website PopUp. Undefined until the
// gate call comes back, and every field has a fallback on the server, so the
// popup can never render half-written.
export interface PopupContent {
  heading: string;
  description: string;
  offer_line: string;
  button_label: string;
  social_proof: string | null;
  logo_url: string | null;
}

interface PopupGate {
  show_popup: boolean;
  content: PopupContent;
}

interface State {
  open: boolean;
  subscribed: boolean;
  content?: PopupContent;
}

// Private-mode browsers throw on storage access, and a newsletter prompt is
// never worth taking a page down for.
const readNumber = (key: string): number => {
  try {
    return parseInt(window.sessionStorage.getItem(key) || "0", 10) || 0;
  } catch (error) {
    return 0;
  }
};

const writeNumber = (key: string, value: number) => {
  try {
    window.sessionStorage.setItem(key, String(value));
  } catch (error) {
    // Ignore — the server-side cookie is the durable guard anyway.
  }
};

// The signup prompt. Desktop gets true exit intent; touch devices get reading
// depth; both get a dwell and a second-page fallback so that leaving in a way
// the cursor cannot report still gets one ask. It asks once and then leaves you
// be — the server remembers a dismissal for a month and a signup for two years.
class NewsletterPopup extends Component<Props, State> {
  private armTimer?: number;
  private confirmationTimer?: number;
  private dwellTimer?: number;
  private dialogRef = React.createRef<HTMLDivElement>();
  private previouslyFocused: HTMLElement | null = null;
  private recorded = false;
  private unmounted = false;
  private armed = false;
  // Set once, when the server confirms this visitor should be asked at all.
  // Every later page re-reads it rather than asking again.
  private eligible = false;

  state: State = {
    open: false,
    subscribed: false,
  };

  componentDidMount() {
    this.countPageView();

    // A preview still has to wait for the content, or it would show the copy we
    // ship with rather than the copy being previewed — which is the one thing it
    // exists to do.
    if (!this.isPreview() && this.shownThisSession()) return;

    getRequest(`/api/newsletter-popup.json`)
      .then((response: AxiosResponse<PopupGate>) => {
        if (this.unmounted) return;

        this.setState({ content: response.data.content });

        // ?preview_popup=1 opens it straight away and records nothing, so the
        // copy can be checked without waiting out the triggers or burning the
        // one ask this visitor gets. It ignores the switch too, so a popup that
        // is currently turned off can still be worked on.
        if (this.isPreview()) {
          this.armed = true;
          this.openPopup("exit_intent");
          return;
        }

        if (!response.data.show_popup) return;

        this.eligible = true;
        this.armTimer = window.setTimeout(this.armTriggers, ARM_DELAY_MS);
      })
      .catch(() => {
        // A newsletter prompt is never worth an error message. Stay quiet.
      });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.pathname === this.props.pathname) return;

    this.countPageView();
    this.restartArming();
  }

  componentWillUnmount() {
    this.unmounted = true;
    window.clearTimeout(this.armTimer);
    window.clearTimeout(this.confirmationTimer);
    this.disarmTriggers();
    document.removeEventListener("keydown", this.onKeyDown);
    document.body.classList.remove("has-newsletter-popup");
  }

  // Counted once per path so that a remount on the same page — which React does
  // freely — cannot inflate its way to the second-page trigger.
  countPageView = () => {
    const path = this.props.pathname || window.location.pathname;

    try {
      if (window.sessionStorage.getItem(LAST_PATH_KEY) === path) return;
      window.sessionStorage.setItem(LAST_PATH_KEY, path);
    } catch (error) {
      return;
    }

    writeNumber(PAGEVIEWS_KEY, readNumber(PAGEVIEWS_KEY) + 1);
  };

  shownThisSession = () => {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) === "true";
    } catch (error) {
      return false;
    }
  };

  markShownThisSession = () => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, "true");
    } catch (error) {
      // Ignore — the server-side cookie is the durable guard anyway.
    }
  };

  // Exactly ?preview_popup=1, not merely the presence of the parameter, so a
  // link carrying ?preview_popup=0 does not open the thing it is asking not to.
  isPreview = () => {
    try {
      return new URLSearchParams(window.location.search).get("preview_popup") === "1";
    } catch (error) {
      return false;
    }
  };

  isCoursePage = () => this.props.pageContext === "course";

  // A promo bar is a live discount, and on a course page it is almost always a
  // better offer than the newsletter's 10%. Showing both would hand someone the
  // worse of the two while they are deciding whether to buy.
  promoBarOnScreen = () => !!document.querySelector(".promo-bar");

  scrollRatio = () =>
    this.isCoursePage() ? COURSE_SCROLL_TRIGGER_RATIO : SCROLL_TRIGGER_RATIO;

  armTriggers = () => {
    if (this.unmounted) return;

    this.armed = true;

    // Both, rather than one or the other by device class. A laptop with a touch
    // screen reports a fine pointer and hover, so picking by that alone gave it
    // exit intent only — and someone scrolling such a machine with a finger
    // never reached the scroll trigger. On a course page, where these are the
    // only two triggers allowed, that left them with nothing at all. Whichever
    // fires first opens it; the other is torn down with it.
    document.addEventListener("mouseout", this.onMouseOut);
    window.addEventListener("scroll", this.onScroll, { passive: true });

    // Course pages get the leaving triggers only. Everywhere else, someone who
    // has browsed for a while has told us they are interested, and that is a
    // better moment to ask than the instant they reach for the tab bar.
    if (this.isCoursePage()) return;
    if (this.maybeOpenOnSecondPage()) return;

    this.dwellTimer = window.setInterval(this.onDwellTick, DWELL_TICK_MS);
  };

  // Every new page gets its own fifteen seconds, rather than inheriting what was
  // left of the last page's. Arriving somewhere and being asked two seconds
  // later reads as a pounce however long the visit has been, and a rule you can
  // hold in your head — "it waits fifteen seconds wherever you land" — is worth
  // more than the handful of extra asks that inheriting the clock would win.
  restartArming = () => {
    if (!this.eligible || this.state.open || this.shownThisSession()) return;

    this.disarmTriggers();
    window.clearTimeout(this.armTimer);
    this.armTimer = window.setTimeout(this.armTriggers, ARM_DELAY_MS);
  };

  // Checked when the triggers arm, which on a second page is fifteen seconds
  // after arriving on it.
  maybeOpenOnSecondPage = (): boolean => {
    if (!this.armed || this.isCoursePage()) return false;
    if (readNumber(PAGEVIEWS_KEY) < 2) return false;

    this.openPopup("second_page");
    return true;
  };

  disarmTriggers = () => {
    this.armed = false;
    document.removeEventListener("mouseout", this.onMouseOut);
    window.removeEventListener("scroll", this.onScroll);
    window.clearInterval(this.dwellTimer);
  };

  // Only counts time the tab is actually in front, so a page left open in a
  // background tab all afternoon never trips the trigger.
  onDwellTick = () => {
    if (document.hidden) return;

    const spent = readNumber(DWELL_KEY) + DWELL_TICK_MS;
    writeNumber(DWELL_KEY, spent);

    if (spent >= DWELL_TRIGGER_MS) {
      // Spend it rather than leave it banked. The session flag is what stops a
      // second appearance, but if that is ever cleared a stale total above the
      // threshold would fire the popup on the very first tick afterwards.
      writeNumber(DWELL_KEY, 0);
      this.openPopup("dwell");
    }
  };

  onMouseOut = (event: MouseEvent) => {
    // Only count the cursor leaving through the top of the window — heading for
    // the tabs, the address bar or the close button. Sideways and downwards are
    // not exits, and a `relatedTarget` means it never left the document at all.
    if (event.clientY > 0 || event.relatedTarget) return;

    this.openPopup("exit_intent");
  };

  onScroll = () => {
    const scrollable =
      document.documentElement.scrollHeight - window.innerHeight;

    if (scrollable <= 0) return;
    if (window.pageYOffset / scrollable < this.scrollRatio()) return;

    this.openPopup("scroll_depth");
  };

  openPopup = (trigger: Trigger) => {
    if (this.state.open || !this.armed) return;
    // Belt and braces: a timer started on a calmer page must not follow someone
    // onto a course page and interrupt them there.
    if (this.isCoursePage() && !LEAVING_TRIGGERS.includes(trigger)) return;
    if (this.isCoursePage() && this.promoBarOnScreen()) return;

    this.disarmTriggers();

    // A preview is not an appearance: it must not count towards the conversion
    // report, and it must not use up this visitor's one ask.
    if (!this.isPreview()) {
      this.markShownThisSession();
      this.recordSeen(false, trigger);
    }

    this.previouslyFocused = document.activeElement as HTMLElement;

    document.addEventListener("keydown", this.onKeyDown);
    document.body.classList.add("has-newsletter-popup");

    this.setState({ open: true }, () => this.dialogRef.current?.focus());
  };

  closePopup = () => {
    window.clearTimeout(this.confirmationTimer);
    document.removeEventListener("keydown", this.onKeyDown);
    document.body.classList.remove("has-newsletter-popup");

    this.setState({ open: false });
    // Hand focus back without moving the page. Focusing an element scrolls it
    // into view by default, and whatever was focused when the popup opened
    // is usually a link the reader clicked long ago -- "View Pricing" at the
    // top of the home page, say -- so closing the popup yanked them back up
    // to it.
    this.previouslyFocused?.focus({ preventScroll: true });
  };

  onSubscribed = () => {
    this.setState({ subscribed: true });
    this.recordSeen(true);

    this.confirmationTimer = window.setTimeout(
      this.closePopup,
      CONFIRMATION_MS,
    );
  };

  // The open is recorded as it happens rather than on dismissal: plenty of
  // people wander off without closing anything, and those are exactly the ones
  // a conversion rate needs to count.
  recordSeen = (subscribed: boolean, trigger?: Trigger) => {
    if (!subscribed && this.recorded) return;

    this.recorded = true;
    postRequest(`/api/newsletter-popup.json`, { subscribed, trigger }).catch(
      () => {
        // Worst case the visitor is asked again next session.
      },
    );
  };

  onKeyDown = (event: globalThis.KeyboardEvent) => {
    if (event.key === "Escape") {
      this.closePopup();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = this.dialogRef.current?.querySelectorAll<HTMLElement>(
      FOCUSABLE,
    );
    if (!focusable?.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Mirrors the shape of the built-in heading so the admin copy lands in the
  // same styles. Falls back to the shared default while the content is still in
  // flight, which is also what a preview sees before the request lands.
  renderHeading = () => {
    const { content } = this.state;
    if (!content) return undefined;

    return (
      <>
        {content.logo_url && (
          <img className="newsletter-popup__logo" src={content.logo_url} alt="" />
        )}
        <h2>{withHighlight(content.heading)}</h2>
        <p className="lead">
          {content.description}
          <span className="lead-offer">{content.offer_line}</span>
        </p>
      </>
    );
  };

  render() {
    if (!this.state.open) return null;

    return createPortal(
      <div className="newsletter-popup">
        {/* Click-to-dismiss only. The close button below is the one screen
            readers and keyboards get. */}
        <button
          className="newsletter-popup__backdrop"
          type="button"
          aria-hidden="true"
          tabIndex={-1}
          onClick={this.closePopup}
        />
        <div
          className="newsletter-popup__card"
          role="dialog"
          aria-modal="true"
          aria-label="Newsletter signup"
          ref={this.dialogRef}
          tabIndex={-1}
        >
          <button
            className="newsletter-popup__close"
            type="button"
            aria-label="Close newsletter signup"
            onClick={this.closePopup}
          >
            &times;
          </button>
          <NewsletterForm
            onSubscribed={this.onSubscribed}
            heading={this.renderHeading()}
            promise={this.state.content?.social_proof}
            buttonLabel={this.state.content?.button_label}
          />
        </div>
      </div>,
      document.body,
    );
  }
}

export default NewsletterPopup;
